import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, Check, Copy, Instagram, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { SettingsPanel, SettingsFieldGrid } from '@/components/settings/SettingsConsole';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import meetingsService from '@/services/meetings.service';
import metaService, { type MetaMessagingSettings } from '@/services/meta.service';

/**
 * Einstellungen → Messenger & Instagram.
 *
 * Beide Kanäle laufen über DIESELBE Facebook-Seite (Instagram ist als
 * Business-Konto mit der Seite verknüpft) — deshalb reicht eine Verbindung:
 * Page-ID + Seiten-Token. Nachrichten werden zu Tickets, Antworten gehen
 * aus dem Ticket zurück (24-h-Fenster wie bei WhatsApp).
 */
export default function MetaMessagingSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Kanal bağlantıları ORG bazlıdır — her shop kendi hesabını bağlar.
  const orgsQuery = useQuery({
    queryKey: ['voice-orgs'],
    queryFn: () => meetingsService.organizations(),
    staleTime: 5 * 60_000,
  });
  const orgs = orgsQuery.data || [];
  const [orgId, setOrgId] = useState('');
  useEffect(() => {
    if (!orgId && orgs.length) setOrgId(orgs[0].id);
  }, [orgs, orgId]);
  const oid = orgId || undefined;

  const { data, isLoading } = useQuery<MetaMessagingSettings>({
    queryKey: ['meta-settings', orgId],
    queryFn: () => metaService.getSettings(oid),
    enabled: Boolean(orgId) || (orgsQuery.isFetched && orgs.length === 0),
  });

  const [pageId, setPageId] = useState('');
  const [pageToken, setPageToken] = useState('');
  const [igAccountId, setIgAccountId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!data) return;
    setPageId(data.config.page_id || '');
    setIgAccountId(data.config.ig_account_id || '');
    setIsActive(Boolean(data.config.is_active));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      metaService.saveSettings({
        ...(pageToken.trim() ? { page_access_token: pageToken.trim() } : {}),
        ...(appSecret.trim() ? { app_secret: appSecret.trim() } : {}),
        page_id: pageId.trim() || null,
        ig_account_id: igAccountId.trim() || null,
        is_active: isActive,
      }, oid),
    onSuccess: (res) => {
      queryClient.setQueryData(['meta-settings', orgId], res);
      setPageToken('');
      setAppSecret('');
      toast.success(t('settings:meta.saved', 'Einstellungen gespeichert'));
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const testMutation = useMutation({
    mutationFn: () => metaService.testSettings(oid),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['meta-settings'] });
      if (r.success) {
        toast.success(t('settings:meta.testOk', 'Verbunden: {{name}}', { name: r.pageName || 'Facebook-Seite' }));
      } else {
        toast.error(r.error || t('settings:meta.testFailed', 'Verbindung fehlgeschlagen'));
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const copy = (value: string, label: string) => {
    void navigator.clipboard.writeText(value);
    toast.success(t('settings:meta.copied', '{{what}} kopiert', { what: label }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const connected = Boolean(data?.connected);

  return (
    <div className="space-y-4">
      {/* --- Organisation wählen (nur bei Mitgliedschaft in mehreren) ------ */}
      {orgs.length > 1 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Building2 className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">
              {t('settings:voice.org.title', 'Organisation')}
            </p>
            <p className="text-xs text-slate-500">
              {t('settings:voice.org.hint', 'Nummer, KI und Einstellungen gelten je Organisation.')}
            </p>
          </div>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-9 w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}


      <SettingsPanel
        title={t('settings:meta.connection.title', 'Facebook-Seite verbinden')}
        description={t(
          'settings:meta.connection.description',
          'Messenger und Instagram-DM laufen über Ihre Facebook-Seite. Page-ID und Seiten-Token stehen in der Meta-App unter Messenger → API-Einstellungen; das Instagram-Business-Konto muss mit der Seite verknüpft sein.',
        )}
        action={
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                <Check className="mr-1 h-3 w-3" />
                {data?.config.display_name || t('settings:meta.state.connected', 'Verbunden')}
              </Badge>
            ) : (
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                {t('settings:meta.state.notConnected', 'Nicht verbunden')}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => testMutation.mutate()}
              disabled={!connected || testMutation.isPending}
            >
              {testMutation.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
              )}
              {t('settings:meta.test', 'Verbindung testen')}
            </Button>
          </div>
        }
      >
        <SettingsFieldGrid>
          <div className="space-y-1.5">
            <Label htmlFor="meta-page-id">{t('settings:meta.pageId', 'Facebook Page-ID')}</Label>
            <Input
              id="meta-page-id"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              placeholder="123456789012345"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meta-token">{t('settings:meta.pageToken', 'Seiten-Access-Token')}</Label>
            <Input
              id="meta-token"
              type="password"
              autoComplete="off"
              value={pageToken}
              onChange={(e) => setPageToken(e.target.value)}
              placeholder={data?.config.page_access_token ? '••••••••  (unverändert)' : 'EAAG…'}
            />
            <p className="text-xs text-slate-500">
              {t('settings:meta.pageTokenHint', 'Token mit pages_messaging (+ instagram_manage_messages für IG). Leer lassen = gespeicherten behalten.')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meta-ig">{t('settings:meta.igAccountId', 'Instagram-Konto-ID (optional)')}</Label>
            <Input
              id="meta-ig"
              value={igAccountId}
              onChange={(e) => setIgAccountId(e.target.value)}
              placeholder="17841400000000000"
            />
            <p className="text-xs text-slate-500">
              {t('settings:meta.igAccountIdHint', 'Nur zur Info — IG-DMs kommen automatisch, sobald das Business-Konto mit der Seite verknüpft ist.')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meta-secret">{t('settings:meta.appSecret', 'App Secret (optional, empfohlen)')}</Label>
            <Input
              id="meta-secret"
              type="password"
              autoComplete="off"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder={data?.config.app_secret ? '••••••••  (unverändert)' : ''}
            />
            <p className="text-xs text-slate-500">
              {t('settings:meta.appSecretHint', 'Aktiviert die Signaturprüfung eingehender Webhooks.')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>{t('settings:meta.activation', 'Messenger & Instagram aktiv')}</Label>
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5">
              <Switch checked={isActive} onCheckedChange={setIsActive} disabled={!connected && !pageToken.trim()} />
              <span className="text-sm text-slate-600">
                {isActive
                  ? t('settings:meta.activeOn', 'Nachrichten werden zu Tickets, Antworten gehen zurück in den Kanal')
                  : t('settings:meta.activeOff', 'Kanäle sind deaktiviert')}
              </span>
            </div>
          </div>
        </SettingsFieldGrid>

        <div className="mt-4 flex justify-end">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common:action.save', 'Speichern')}
          </Button>
        </div>
      </SettingsPanel>

      {/* Webhook-Daten — erst nach dem ersten Speichern vorhanden */}
      {data?.config.verify_token && (
        <SettingsPanel
          title={t('settings:meta.webhook.title', 'Webhook bei Meta eintragen')}
          description={t(
            'settings:meta.webhook.description',
            'Meta-App → Messenger → Webhooks (und Instagram → Webhooks): beide Werte eintragen und das Feld „messages" abonnieren — für Messenger UND Instagram getrennt.',
          )}
        >
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t('settings:meta.webhookUrl', 'Callback-URL')}</Label>
              <div className="flex gap-2">
                <Input readOnly value={data.webhookUrl} className="font-mono text-xs" />
                <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(data.webhookUrl, 'Callback-URL')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('settings:meta.verifyToken', 'Verify-Token')}</Label>
              <div className="flex gap-2">
                <Input readOnly value={data.config.verify_token} className="font-mono text-xs" />
                <Button variant="outline" size="icon" className="shrink-0" onClick={() => copy(data.config.verify_token!, 'Verify-Token')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="flex items-start gap-2 text-xs text-slate-500">
              <Instagram className="mt-0.5 h-3.5 w-3.5 shrink-0 text-pink-500" />
              {t(
                'settings:meta.reviewHint',
                'Wichtig: Im Entwicklungsmodus können nur Tester der Meta-App schreiben. Für echte Kunden braucht die App den Meta-App-Review (pages_messaging, instagram_manage_messages) und Business-Verifizierung. Antworten sind wie bei WhatsApp nur im 24-Stunden-Fenster möglich.',
              )}
            </p>
          </div>
        </SettingsPanel>
      )}
    </div>
  );
}
